import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
  ITrader,
  TRADER_RATE_MAX,
  TTraderRateField,
  useChangeTraderSettingsMutation,
  validateTraderRate,
} from '@entities/trades/trader';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import { Tooltip } from '@mui/material';
import { useTypedFormError } from '@shared/components/Form';

import './ChangeTraderRate.css';

interface IProps {
  trader: ITrader;
  field: TTraderRateField;
  refetch: () => void;
}

export const ChangeTraderRate = ({ trader, field, refetch }: IProps) => {
  const [changeSettings, { isLoading, isError, error, reset }] = useChangeTraderSettingsMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [validationError, setValidationError] = useState('');

  const { renderErrorMessage } = useTypedFormError(isError, error);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const currentValue = trader[field];
  const errorMessage =
    validationError || renderErrorMessage(field) || renderErrorMessage('general');
  const btnStateClass = isLoading ? 'is-disabled' : 'is-enabled';

  const startEditing = () => {
    setValue(String(currentValue));
    setValidationError('');
    reset();
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setValidationError('');
    reset();
  };

  const handleChange = (nextValue: string) => {
    setValue(nextValue);
    setValidationError('');
    reset();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') stopEditing();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationMessage = validateTraderRate(value, field);
    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }

    const next = Number(value);
    if (next === Number(currentValue)) {
      stopEditing();
      return;
    }

    const result = await changeSettings({ id: trader.id, field, value: next });
    if ('data' in result) {
      stopEditing();
      refetch();
    }
  };

  return (
    <div className='trader-rate-cell'>
      {!isEditing ? (
        <button type='button' className='trader-rate-cell__value' onClick={startEditing}>
          {currentValue}%
        </button>
      ) : (
        <form className='trader-rate-cell__form' onSubmit={handleSubmit}>
          <input
            type='number'
            name={field}
            ref={inputRef}
            min={0}
            max={TRADER_RATE_MAX[field]}
            step={0.1}
            value={value}
            onChange={e => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />

          <div className='action-btns'>
            <Tooltip title='Save'>
              <span>
                <button
                  type='submit'
                  className={`btn-approve ${btnStateClass}`}
                  disabled={isLoading}
                >
                  <DoneIcon />
                </button>
              </span>
            </Tooltip>
            <Tooltip title='Cancel'>
              <span>
                <button
                  type='button'
                  className={`btn-reject ${btnStateClass}`}
                  onClick={stopEditing}
                  disabled={isLoading}
                >
                  <ClearIcon />
                </button>
              </span>
            </Tooltip>
          </div>

          {errorMessage && <div className='trader-rate-cell__error'>{errorMessage}</div>}
        </form>
      )}
    </div>
  );
};
